<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted } from "vue";

import { useComponentErrorCapture } from "@/mixins/error";
import { useTiming } from "@/mixins/timing";
import { isTouchEnabled } from "@/mixins/device";
import { useModal } from "@/mixins/modal";

import { appStateStore } from "@/stores/appState";
import { hideMessage } from "@/stores/messages";

const { sleep } = useTiming();
const { registerOpenModal, unregisterModal } = useModal();
useComponentErrorCapture();

const emit = defineEmits(["close", "closing", "submit"]);

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  show: {
    type: Boolean,
    default: false,
  },
  bodyCover: {
    type: Boolean,
    default: true,
  },
  closeable: {
    type: Boolean,
    default: true,
  },
  buttonBack: {
    type: Boolean,
    default: false,
  },
  width: {
    type: String,
    default: "100%",
  },
  height: {
    type: String,
    default: "100%",
  },
  cols: {
    type: Boolean,
    default: false,
  },
});

const modal = ref<HTMLDivElement | null>(null);
const appState = appStateStore();

// For animation
const showModal = ref(false);

watch(props, async () => {
  if (props.show) {
    registerOpenModal(props.name, !props.closeable);
    showModal.value = true;
    
    if (!isTouchEnabled()) {
      await sleep(200);
      const focusElement = modal.value as HTMLDivElement;
      let input;
      if (focusElement) {
        input = focusElement.querySelector("input, textarea") as HTMLDivElement;
        if (input?.closest("[data-nofocus]")) {
          return;
        }
      }
      (input ? input : focusElement)?.focus();
    }
  } else {
    unregisterModal(props.name, !props.closeable);
  }
});

appState.$subscribe((mutation, state) => {
  if (props.show && !state.modalPool.includes(props.name) && props.closeable) {
    triggerClose();
  }

  if (state.modalPool.length > 0) {
    document.body.classList.add('overflow-hidden');
  } else {
    document.body.classList.remove('overflow-hidden');
  }
});

async function triggerClose() {
  if (!props.closeable) {
    return;
  }
  emit("closing");

  showModal.value = false;
  await sleep(800);
  hideMessage()
  emit("close");
}

function triggerSubmit() {
  emit("submit");
}

function getCloseButtonClasses(device: string) {
  return props.buttonBack ? ` modal__back modal__back--${device}` : ` modal__close modal__close--${device}` 
}

onMounted(async () => {
  showModal.value = props.show;
});

onUnmounted(() => {
  unregisterModal(props.name, !props.closeable);
});

</script>
<template>
  <Transition name="opacity-fast">
    <div
      v-if="show"
      v-show="showModal"
      class="modal"
      @keyup.enter.stop="triggerSubmit"
      ref="modal"
    >
      <div class="modal__wrapper">
        <div>
          <button
            @click="triggerClose"
            class="button button--tertiary button--icon"
            :class="getCloseButtonClasses('desktop')">
            <i class="fa-regular " :class="buttonBack ? 'fa-arrow-left' : 'fa-close'"></i>
          </button>
          <button
            @click="triggerClose"
            class="button button--secondary button--icon button--icon--small"
            :class="getCloseButtonClasses('phone')">
            <i class="fa-regular " :class="buttonBack ? 'fa-arrow-left' : 'fa-close'"></i>
          </button>
        </div>
        <div class="modal__container" :style="`max-width:${props.width};height:${props.height};`">
          <div v-if="!!$slots.header" class="modal__header" :class="{'modal__header--back-button' : buttonBack}">
            <slot name="header" />
          </div>

          <div v-if="bodyCover" class="modal__body" :class="{'modal__body--cols': cols == true}">
            <slot />
          </div>
          <slot v-if="!bodyCover" />
          <div class="modal__footer" v-if="!!$slots.footer">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>
